import React from 'react';
import PropTypes from 'prop-types';
import {RichText} from 'prismic-reactjs';
import {SliceNames} from '../../utils';

/*
    While we may have multiple content slices containing FAQs on a single page,
    there must only be one FAQPage type definition on a single page.
    This consolidates questions and answers from FAQ slices and produces a single FAQPage schema.

    Additional guidelines for using FAQPage structured data: https://developers.google.com/search/docs/data-types/faqpage
*/
export const extractFAQItems = slices => {
    return slices.reduce((accmFAQ, currentSlice) => {
        switch (currentSlice.slice_type) {
            case SliceNames.FAQ_WITH_TITLE:
            case SliceNames.FAQ:
                return accmFAQ.concat(
                    currentSlice.items.map(faq => ({
                        question: faq.question,
                        /*
                            NOTE: For the rich text in the FAQ answers to render as a string in the structured data
                            it must use RichText.asText instead of RichText.render
                        */
                        answer: RichText.asText(faq.answer),
                    }))
                );
            default:
                return accmFAQ;
        }
    }, []);
};

export const formatFAQPageSchema = faqItems => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(faq => ({
            '@type': 'Question',
            name: `${faq.question}`,
            acceptedAnswer: {
                '@type': 'Answer',
                text: `${faq.answer}`,
            },
        })),
    };
};

const FAQPageStructuredData = ({slices}) => {
    const faqItems = extractFAQItems(slices);

    if (faqItems.length === 0) {
        return null;
    } else {
        return (
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(formatFAQPageSchema(faqItems)),
                }}
            />
        );
    }
};

/* eslint-disable camelcase */
FAQPageStructuredData.propTypes = {
    slices: PropTypes.arrayOf(
        PropTypes.shape({
            slice_type: PropTypes.string.isRequired,
        })
    ).isRequired,
};

export default FAQPageStructuredData;
